import React, { useRef } from 'react';
import {
  Box,
  Card,
  CardContent,
  Chip,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { useSnackbar } from 'notistack';
import CustomButton from '../../../../components/General/CustomButton';
import useStyles from '../styles';
import GoogleMaps from '../../../../components/Layout/Google/GoogleMaps';
import { ReduxState } from '../../../../types';
import { setFarmDevice, setSensorNodeFilter } from '../../../../actions/farmSectorActions';
import { filteredDevices, filteredDevicesBySector, handleKMZFile } from '../helpers';
import { PutSectorsKMZSchema } from '../../../../requests/api/apiTypes';
import { setCurrentSector } from '../../../../actions/currentSectorActions';
import useNodes from '../../../../hooks/useNodes';
import useSensors from '../../../../hooks/useSensors';
import { Equipo } from '../../Riego/equipoColors';

interface LayoutMapProps {
  setOpenUploadKMZModal: React.Dispatch<React.SetStateAction<{
    open: boolean, data: null | Array<PutSectorsKMZSchema[string]>
  }>>;
  setKMZSector: React.Dispatch<React.SetStateAction<null | PutSectorsKMZSchema[string]>>;
  hideDeviceFilters?: boolean;
  isLayout?: boolean;
  equipos?: Equipo[];
  equipoBySectorId?: Record<string, Equipo>;
  irrigatingSectorIds?: Set<string>;
}

const REGANDO_STROKE = '#00E5FF';
const REGANDO_STROKE_WEIGHT = 5;
const REGANDO_FILL_OPACITY = 0.55;
const REGANDO_BADGE_ACCENT = '#0E7490';
const SELECTED_STROKE = '#FFFFFF';

const LayoutMap = ({
  setOpenUploadKMZModal,
  setKMZSector,
  hideDeviceFilters,
  isLayout = true,
  equipos,
  equipoBySectorId,
  irrigatingSectorIds,
}: LayoutMapProps) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const KMZInputRef = useRef<HTMLInputElement>(null);
  const { enqueueSnackbar } = useSnackbar();
  const { selectedFarm } = useSelector((state: ReduxState) => state.accountFarm);
  const { selectedSector } = useSelector((state: ReduxState) => state.currentSector);
  const { nodes } = useNodes(selectedFarm?.id ?? '');
  const { sensors } = useSensors(selectedFarm?.id ?? '');
  const allFarmDevices = [...nodes, ...sensors];

  const {
    selectedDevice, sensorNodeFilter,
  } = useSelector((state: ReduxState) => state.farmSector);

  const handleSectorClick = (sectorId: string) => {
    if (selectedSector?.id === sectorId) {
      dispatch(setCurrentSector(null));
    } else {
      // Find the sector object in the selectedFarm
      const selectedSectorObj = selectedFarm?.sectors?.find((sector) => sector.id === sectorId);

      if (selectedSectorObj) {
        dispatch(setCurrentSector(selectedSectorObj)); // Set current sector in Redux
      }
    }
  };

  const handleDeviceClick = (deviceId: string) => {
    if (selectedDevice === deviceId) {
      dispatch(setFarmDevice(''));
      dispatch(setCurrentSector(null));
    } else {
      dispatch(setFarmDevice(deviceId));
    }
  };

  const colorByEquipo = !!equipos?.length && !!equipoBySectorId;
  const hasRegando = !!irrigatingSectorIds && irrigatingSectorIds.size > 0;

  const polygonsAux = selectedFarm?.sectors?.map((sector) => {
    const isSelected = Boolean(!selectedSector
      || selectedSector.id === sector.id
      || (selectedSector.parentSector
        && sector.parentSector === selectedSector.parentSector
        && !isLayout));
    const equipo = sector.id ? equipoBySectorId?.[sector.id] : undefined;
    const isThisSelected = selectedSector?.id === sector.id;
    const isRegando = !!sector.id && !!irrigatingSectorIds?.has(sector.id);
    return {
      id: sector.id,
      coordinates: sector?.coordinates ?? [],
      isSelected,
      onPolygonClick: () => handleSectorClick(sector.id ?? ''),
      ...(colorByEquipo && equipo
        ? {
          fillColor: equipo.color,
          strokeColor: isThisSelected ? SELECTED_STROKE : equipo.color,
        }
        : {}),
      ...(isRegando
        ? {
          strokeColor: REGANDO_STROKE,
          strokeWeight: REGANDO_STROKE_WEIGHT,
          fillOpacity: REGANDO_FILL_OPACITY,
          // Gota flotante sobre el sector que está regando.
          badge: { textMain: 'Regando', icon: '💧', accent: REGANDO_BADGE_ACCENT },
        }
        : {}),
    };
  }) ?? [];

  const markersAux = filteredDevices(allFarmDevices, sensorNodeFilter)
    .filter((device) => filteredDevicesBySector(device, selectedSector?.id ?? ''))
    .map((device) => ({
      // eslint-disable-next-line no-underscore-dangle
      id: device.id || device._id,
      coordinates: device.coordinates,
      isSelected: device.id === selectedDevice,
      deviceType: 'nodeType' in device ? 'node' : 'sensor',
      isGatewayNode: 'nodeType' in device ? device.isGatewayNode : false,
      onMarkerClick: () => handleDeviceClick(device.id ?? ''),
    })) ?? [];

  const farmCoordinates = selectedFarm?.coordinates
    ? selectedFarm.coordinates.map(({ lat: farmLat, lng: farmLng }) => ({
      lat: typeof farmLat === 'string' ? parseFloat(farmLat) : farmLat,
      lng: typeof farmLng === 'string' ? parseFloat(farmLng) : farmLng,
    })) : null;

  const disabledLogic = () => {
    // KMZ can only be used on farms that have all sectors without coordinates
    if (!selectedFarm
      || selectedFarm?.sectors?.some((sector) => (sector?.coordinates?.length ?? 0) > 0)) {
      return true;
    }

    return false;
  };

  const handleKMZChange = async () => {
    const relevantFile = KMZInputRef?.current?.files?.[0];
    if (!relevantFile) {
      enqueueSnackbar('Archivo KMZ no pudo ser cargado correctamente', { variant: 'error' });
      return;
    }
    const validExtensions = ['.kmz', '.kml'];

    const fileExtension = relevantFile.name.toLowerCase().slice(relevantFile.name.lastIndexOf('.'));
    if (!validExtensions.includes(fileExtension)) {
      enqueueSnackbar('Archivo cargado no es del tipo KMZ o KML', { variant: 'error' });
      return;
    }

    try {
      const validPolygons = await handleKMZFile(relevantFile, setKMZSector);
      if (KMZInputRef?.current) KMZInputRef.current.value = '';
      setOpenUploadKMZModal({ open: true, data: validPolygons });
    } catch (err) {
      const error = err as Error;
      enqueueSnackbar(error.message, { variant: 'error' });
    }
  };

  const handleAddKMZ = () => {
    if (KMZInputRef.current) {
      KMZInputRef.current.click();
    }
  };

  return (
    <Card className={classes.cardLayoutContainer}>
      <CardContent className={classes.cardLayoutContentContainer}>
        <Box className={classes.cardHeaderLayoutContentContainer}>
          <Box display='flex' alignItems='center'>
            <Box display='flex' alignItems='center' marginRight='20px'>
              <Typography
                variant="h4"
                color="primary"
              >
                Mapa
              </Typography>
            </Box>
            {!hideDeviceFilters && (
              <Box>
                <CustomButton
                  buttonText='subir kmz'
                  onClick={handleAddKMZ} color='blue' size='small'
                  disabled={disabledLogic()}
                />
                <input
                  type="file"
                  ref={KMZInputRef}
                  style={{ display: 'none' }}
                  accept=".kmz,application/vnd.google-earth.kmz"
                  onChange={handleKMZChange}
                />
              </Box>
            )}
          </Box>
          {!hideDeviceFilters && (
            <Box display='flex' paddingLeft='10px'>
              <Box display='flex' alignItems='center' flexWrap='wrap'>
                {sensorNodeFilter.deviceType === 'node'
                  ? <>
                    <Chip key='gateway'
                      color={sensorNodeFilter.filterValue === 'gateway' ? 'primary' : undefined}
                      label="Gateway" onClick={() => dispatch(setSensorNodeFilter({ ...sensorNodeFilter, filterValue: 'gateway' }))}
                      size='small' className={classes.layoutMapChip} />
                    <Chip key='noGateway'
                      color={sensorNodeFilter.filterValue === 'noGateway' ? 'primary' : undefined}
                      label="No Gateway" onClick={() => dispatch(setSensorNodeFilter({ ...sensorNodeFilter, filterValue: 'noGateway' }))}
                      size='small' className={classes.layoutMapChip} />
                    <Chip key='todosNodo'
                      color={sensorNodeFilter.filterValue === '' ? 'primary' : undefined}
                      label="Todos" onClick={() => dispatch(setSensorNodeFilter({ ...sensorNodeFilter, filterValue: '' }))}
                      size='small' className={classes.layoutMapChip} />
                  </>
                  : <>
                    <Chip key='suelo'
                      color={sensorNodeFilter.filterValue === 'suelo' ? 'primary' : undefined}
                      label="Suelo"
                      onClick={() => dispatch(setSensorNodeFilter({ ...sensorNodeFilter, filterValue: 'suelo' }))}
                      size='small' className={classes.layoutMapChip} />
                    <Chip key='clima'
                      color={sensorNodeFilter.filterValue === 'clima' ? 'primary' : undefined}
                      label="Clima" onClick={() => dispatch(setSensorNodeFilter({ ...sensorNodeFilter, filterValue: 'clima' }))}
                      size='small' className={classes.layoutMapChip} />
                    <Chip key='planta'
                      color={sensorNodeFilter.filterValue === 'planta' ? 'primary' : undefined}
                      label="Planta" onClick={() => dispatch(setSensorNodeFilter({ ...sensorNodeFilter, filterValue: 'planta' }))}
                      size='small' className={classes.layoutMapChip} />
                    <Chip key='riego'
                      color={sensorNodeFilter.filterValue === 'riego' ? 'primary' : undefined}
                      label="Riego" onClick={() => dispatch(setSensorNodeFilter({ ...sensorNodeFilter, filterValue: 'riego' }))}
                      size='small' className={classes.layoutMapChip} />
                    <Chip key='todosSensor'
                      color={sensorNodeFilter.filterValue === '' ? 'primary' : undefined}
                      label="Todos" onClick={() => dispatch(setSensorNodeFilter({ ...sensorNodeFilter, filterValue: '' }))}
                      size='small' className={classes.layoutMapChip} />
                  </>
                }
              </Box>
              <Box>
                <Tabs
                  value={sensorNodeFilter.deviceType}
                  onChange={(e, value) => dispatch(setSensorNodeFilter({ deviceType: value, filterValue: '' }))}
                  textColor="secondary"
                  indicatorColor="secondary"
                  aria-label="secondary tabs example"
                >
                  <Tab value="node" label="Nodos" />
                  <Tab value="sensor" label="Sensores" />
                </Tabs>
              </Box>
            </Box>
          )}
        </Box>
        {/* Leyenda de equipos de riego (nombre + muestra de color), consistente con el mapa */}
        {(colorByEquipo || hasRegando) && (
          <Box sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 1.5,
            mt: 1,
            px: 0.5,
            alignItems: 'center',
          }}>
            {colorByEquipo && (equipos ?? []).map((equipo) => (
              <Box key={equipo.id} sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <Box sx={{
                  width: 12,
                  height: 12,
                  borderRadius: '2px',
                  backgroundColor: equipo.color,
                  border: '1px solid rgba(0,0,0,0.2)',
                  flexShrink: 0,
                }} />
                <Typography variant="caption" sx={{ lineHeight: 1 }}>
                  {equipo.name}
                </Typography>
              </Box>
            ))}
            {/* Swatch de borde brillante = sectores regando ahora. */}
            {hasRegando && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <Box sx={{
                  width: 12,
                  height: 12,
                  borderRadius: '2px',
                  backgroundColor: 'transparent',
                  border: `2px solid ${REGANDO_STROKE}`,
                  flexShrink: 0,
                }} />
                <Typography variant="caption" sx={{ lineHeight: 1 }}>
                  💧 Regando ahora
                </Typography>
              </Box>
            )}
          </Box>
        )}
        {selectedFarm
          && <Box className={classes.googleMapsContainer}>
            <GoogleMaps
              lat={selectedFarm.center.lat}
              lng={selectedFarm.center.lng}
              drawable='none'
              farm={{
                id: selectedFarm?.id ?? '',
                coordinates: farmCoordinates,
                zoom: selectedFarm?.zoom ?? 18,
              }}
              polygons={polygonsAux}
              markers={markersAux}
              autoFit={!isLayout}
            />
          </Box>}
        {!selectedFarm && <Typography variant='body1'>
          Selecciona un campo para poder visualizar el mapa
        </Typography>}
      </CardContent>
    </Card>
  );
};

export default LayoutMap;
